import type {BoardLabels} from '@github-ui/board-core'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'

import {useAnnounceOnce} from './announce-once'
import styles from './BoardSkeleton.module.css'
import {BoardCardSkeleton} from './BoardSkeletons'

/**
 * How much of a board is drawn while one is on its way. Enough to fill a wide viewport
 * and no more: the skeleton is clipped rather than scrolled, so a placeholder past the
 * fold is a subtree nobody sees.
 */
const MOST_COLUMNS = 6
const MOST_ROWS = 2
const CARDS_PER_CELL = 4

/** What a board with nothing on screen yet is drawn as. */
const DEFAULT_COLUMNS = 3

function positions(length: number): number[] {
  return Array.from({length}, (_, index) => index)
}

export type BoardSkeletonProps = {
  /**
   * Every string this draws. The same set `<Board>` is given, because this renders in
   * place of one and outside its provider, so there is nothing to read them off.
   */
  labels: BoardLabels
  /**
   * How many placeholder columns to draw. Shape it from the board being replaced so the
   * swap costs the reader no movement. Capped, and defaulted for a board that has none.
   */
  columns?: number
  /**
   * How many placeholder swimlanes to draw, each behind a row header.
   *
   * Omit it on a board with no swimlane, which draws one band and no row header. That is
   * the same rule `renderRowHeader` follows on `<Board>`: passing nothing is what says
   * the board has no rows a reader sees.
   */
  rows?: number
  /**
   * The maximum height `<Board>` is given, as a CSS length. A placeholder uses the full
   * maximum because it cannot know the cards yet; a short swimlane may contract when the
   * board arrives. Defaults to `100%`, which is the whole of the box and what a board
   * with no swimlane wants.
   */
  rowHeight?: string
  className?: string
}

/**
 * A whole board, drawn as placeholders, for a board whose columns, rows and cards are all
 * being replaced.
 *
 * Distinct from the skeletons in `BoardSkeletons.tsx`, which each stand for one page
 * arriving into a board that is already on screen. Nothing here stands for a page: a
 * board changed at the axis is a board where every name and every card on screen is about
 * to be wrong, so it is replaced rather than appended to.
 *
 * **It draws no `<Board>` and mounts no provider.** A board built over placeholder cards
 * would announce them, let a reader select one and let a card be dragged to a cell that
 * does not exist. So this is markup shaped like a board, and its cards are neither a list
 * nor focusable.
 *
 * Sized by the box it is put in, and it scrolls and overflows the way the board does, so
 * the page holds still across the swap and so does the board's own lower edge.
 */
export function BoardSkeleton({labels, columns, rows, rowHeight, className}: BoardSkeletonProps) {
  const columnCount = Math.min(columns || DEFAULT_COLUMNS, MOST_COLUMNS)
  const rowCount = rows === undefined ? 1 : Math.min(Math.max(rows, 1), MOST_ROWS)
  const hasRowHeaders = rows !== undefined

  // Mounted only while the board is on its way, so arriving here is the transition.
  // Announced rather than drawn into a live region, for the reason `useAnnounceOnce`
  // gives, and announced at all because a board is replaced without taking focus.
  useAnnounceOnce(labels.loading, true)

  return (
    <div
      className={clsx(styles.Skeleton, className)}
      style={rowHeight === undefined ? undefined : {'--board-row-block-size': rowHeight}}
      data-row-headers={hasRowHeaders ? 'true' : undefined}
      data-testid="board-skeleton"
    >
      {/*
        A real text node rather than an `aria-label` on the container, for the reason
        `BoardCardSkeletons` gives: the bars are decorative and hidden, so a name put on a
        box with no content behind it is dropped from the reading order entirely.
      */}
      <VisuallyHidden>{labels.loading}</VisuallyHidden>
      <div className={styles.ColumnHeaders} aria-hidden>
        {positions(columnCount).map(column => (
          <div key={column} className={styles.ColumnHeader}>
            <LoadingSkeleton height="md" width="random" variant="rounded" />
          </div>
        ))}
      </div>
      <div className={styles.Rows} aria-hidden>
        {positions(rowCount).map(row => (
          // Carries a test id for the reason `BoardCardSkeleton` does: it is hidden and
          // has no role or name, and what has to be asserted about it is the box.
          <div key={row} className={styles.Row} data-testid="board-skeleton-row">
            {hasRowHeaders ? (
              <div className={styles.RowHeader}>
                <LoadingSkeleton height="md" width="8rem" variant="rounded" />
              </div>
            ) : null}
            <div className={styles.Cells}>
              {positions(columnCount).map(column => (
                <div key={column} className={styles.Cell}>
                  {positions(CARDS_PER_CELL).map(card => (
                    <BoardCardSkeleton key={card} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
